import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Translate } from 'react-localize-redux';
import { useDispatch, useSelector } from 'react-redux';
import PasswordCtrl from '@webapp/controllers/registration/password';
import ButtonArrow from '@components/actions/arrow-button';
import AppState from '@webapp/store/state';
import { CreateAdditionalAccount } from '@webapp/store/user/action-creators';
import {
  CREATE_ADDITIONAL_ACCOUNT,
  CREATE_ADDITIONAL_ACCOUNT_ERROR,
} from '@webapp/store/user/actions';
import { isPassword } from '@webapp/utils/validators';
import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Hint from '@components/basics/hint';

type UserSetPasswordCtrlProps = {
  className?: string;
};
const errorKey = false;

export const UserCreatePasswordCtrl: React.FC<UserSetPasswordCtrlProps> = ({
  className,
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const [password, setPassword] = React.useState('');
  const [passwordMissing, setPasswordMissing] = React.useState(false);
  const [invalidPassword, setInvalidPassword] = React.useState('');

  const isLoading = useSelector(
    (state: AppState) => !!state.ui.loading[CREATE_ADDITIONAL_ACCOUNT]
  );
  const passwordError = useSelector(
    (state: AppState) => !!state.ui.errors[CREATE_ADDITIONAL_ACCOUNT_ERROR]
  );

  useEffect(() => {
    if (passwordError) {
      setInvalidPassword('USER.SETTINGS.SECURITY.INPUT.NEW_PASSWORD.ERROR');
    }
  }, [passwordError]);

  const createNewPassword = () => {
    dispatch(new CreateAdditionalAccount(password));
  };

  const updatePassword = (newPassword: string) => {
    setPassword(newPassword);

    if (!newPassword.length) {
      setInvalidPassword(
        'USER.SETTINGS.SECURITY.INPUT.NEW_PASSWORD.EMPTY_ERROR'
      );
      return;
    }

    if (!isPassword(newPassword)) {
      setInvalidPassword('USER.SETTINGS.SECURITY.INPUT.NEW_PASSWORD.INVALID');
      return;
    }

    setInvalidPassword('');
  };

  return (
    <FormContainer
      className={className || ''}
      autoComplete="off"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (!invalidPassword) {
          createNewPassword();
        }
      }}
    >
      <WrapperInputs className={errorKey ? 'has-error' : ''}>
        <InputsContainer
          alignItems="center"
          justifyContent="space-between"
          gap={10}
        >
          <Grid className="inputs-wrapper" gap={5}>
            <PasswordCtrl
              password={password}
              isGrey
              onChange={updatePassword}
              onFocus={() => setPasswordMissing(false)}
              error={
                passwordMissing ? (
                  <Translate id="USER.SETTINGS.SECURITY.INPUT.NEW_PASSWORD" />
                ) : invalidPassword ? (
                  <Translate id={invalidPassword} />
                ) : undefined
              }
            />
          </Grid>
          <ButtonArrow
            id="login"
            type="submit"
            className="button-arrow"
            isLoading={isLoading}
            disabled={!password || !!invalidPassword || passwordMissing}
          />
        </InputsContainer>
      </WrapperInputs>
      <div>
        <Grid gap={3}>
          <Hint
            noMargin
            customTitle={<Translate id="AUTH.LOGIN.PASSWORD.HINT.TITLE" />}
          >
            <Translate id="AUTH.LOGIN.PASSWORD.HINT.TEXT" />
          </Hint>
        </Grid>
      </div>
    </FormContainer>
  );
};

export const FormContainer = styled.form`
  .inputs-wrapper {
    width: 100%;
  }
  .button-arrow {
    max-width: 78px;
  }
`;

export const WrapperInputs = styled(Flex)`
  margin-bottom: 20px;
  width: 100%;
  &.has-error {
    margin-bottom: 20px;
  }
`;

const InputsContainer = styled(Flex)`
  width: 100%;
`;
